// Client side of minimal login-session tracking (Usage & Cost Tracking, doc §7).
// One session per browser/device: opened at login (or silent resume), reattached
// by tabs/refresh, closed on explicit logout / browser unload / session expiry.
// The session_id is kept in localStorage so tabs in the same browser share it.
import { authHeaders } from './authHeader';

const SESSION_ID_KEY = 'rsaUsageSessionId';

function getStoredSessionId() {
  try {
    return localStorage.getItem(SESSION_ID_KEY) || null;
  } catch {
    return null;
  }
}

function storeSessionId(id) {
  try {
    if (id) localStorage.setItem(SESSION_ID_KEY, id);
  } catch {
    /* storage unavailable */
  }
}

function clearStoredSessionId() {
  try {
    localStorage.removeItem(SESSION_ID_KEY);
  } catch {
    /* storage unavailable */
  }
}

/**
 * Open (or reattach to) the login session. Idempotent: passes any stored
 * session_id so a refresh/new tab reattaches instead of duplicating.
 * Best-effort — never throws, never blocks login.
 */
export async function openUsageSession() {
  try {
    const existing = getStoredSessionId();
    const res = await fetch('/api/sessions/open', {
      method: 'POST',
      headers: await authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(existing ? { session_id: existing } : {}),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (data?.session_id) {
      storeSessionId(data.session_id);
      return data.session_id;
    }
  } catch {
    /* best-effort */
  }
  return null;
}

/** Refresh the server-side liveness key. Called on the fixed 15-min timer. */
export async function touchUsageSession() {
  const sessionId = getStoredSessionId();
  if (!sessionId) return;
  try {
    await fetch('/api/sessions/touch', {
      method: 'POST',
      headers: await authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ session_id: sessionId }),
    });
  } catch {
    /* best-effort */
  }
}

/**
 * Close the session with a reason. Awaitable path (explicit logout / session
 * expiry). Clears the stored id so the next login opens a fresh session.
 */
export async function closeUsageSession(reason = 'unknown') {
  const sessionId = getStoredSessionId();
  if (!sessionId) return;
  try {
    await fetch('/api/sessions/close', {
      method: 'POST',
      headers: await authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ session_id: sessionId, reason }),
    });
  } catch {
    /* best-effort */
  } finally {
    clearStoredSessionId();
  }
}

/**
 * Fire-and-forget close via navigator.sendBeacon for the browser-unload path
 * (fetch is unreliable during unload). No auth header — the endpoint accepts a
 * beacon close by session_id (see usage_routes.close_session). Best-effort:
 * beforeunload also fires on refresh/navigation, so the server reattaches a
 * just-closed session if the user is only refreshing.
 */
export function beaconCloseUsageSession(reason = 'browser_closed') {
  const sessionId = getStoredSessionId();
  if (!sessionId) return;
  try {
    const blob = new Blob(
      [JSON.stringify({ session_id: sessionId, reason })],
      { type: 'application/json' }
    );
    navigator.sendBeacon('/api/sessions/close', blob);
  } catch {
    /* best-effort */
  }
}
